import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Loading from '../../../components/loading';
import ModalErrorMessage from '../../../components/modalErrorMessage/modalErrorMessage';
import { useUploadImage } from './hooks/useUploadImage';
import { ACCEPTED_CONTENT_TYPES, PDF_CONTENT_TYPE } from './fileType';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function ImageModal({ open, onCloseImageModal, onImageSaved, patientId }) {
  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const { upload } = useUploadImage();
  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [date, setDate] = useState(dayjs());
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {}, [open]);

  const fileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setShowErrorMessage(true);
      setErrorMessage(`O arquivo excede o limite de ${MAX_FILE_SIZE_MB} MB.`);
      event.target.value = '';
      return;
    }

    if (!ACCEPTED_CONTENT_TYPES.includes(file.type)) {
      setShowErrorMessage(true);
      setErrorMessage('Formato não suportado. Envie uma imagem JPG, PNG ou um arquivo PDF.');
      event.target.value = '';
      setSelectedFile(null);
      setPreview(null);
      return;
    }

    setSelectedFile(file);
    if (file.type === PDF_CONTENT_TYPE) {
      setPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveImage = () => {
    if (!selectedFile || !description || !date) {
      setShowErrorMessage(true);
      setErrorMessage('Por favor, adicione um arquivo e uma descrição.');
      return;
    }

    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('description', description);
    formData.append('date', dayjs(date).format('DD/MM/YYYY'));
    formData.append('file', selectedFile);

    setLoading(true);
    upload(formData)
      .then((returnFromApi) => {
        setLoading(false);
        onImageSaved(returnFromApi);
        cleanFields();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        onImageSaved(returnFromApi);
        console.error(returnFromApi);
      });
  };

  const cancel = () => {
    cleanFields();
    onCloseImageModal();
  };

  function cleanFields() {
    setDate(dayjs());
    setDescription('');
    setSelectedFile(null);
    setPreview(null);
  }

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <Loading isToHidden={isLoading === false} />
      {showErrorMessage && (
        <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
      )}

      <DialogTitle>Adicionar Novo Arquivo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <DatePicker
            label="Data"
            value={date}
            onChange={(newDate) => setDate(newDate)}
            format="DD/MM/YYYY"
          />

          <TextField
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            fullWidth
            required
          />
          <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}>
            Selecionar Arquivo
            <VisuallyHiddenInput
              type="file"
              accept={ACCEPTED_CONTENT_TYPES.join(', ')}
              onChange={fileChange}
            />
          </Button>
          <Typography variant="caption" color="text.secondary">
            Formatos aceitos: JPG, PNG e PDF (até {MAX_FILE_SIZE_MB} MB).
          </Typography>

          {selectedFile?.type === PDF_CONTENT_TYPE && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <PictureAsPdfIcon color="error" />
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {selectedFile.name}
              </Typography>
            </Box>
          )}

          {preview && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Pré-visualização:</Typography>
              <img
                src={preview}
                alt="Pré-visualização"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  marginTop: '10px',
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel}>Cancelar</Button>
        <Button onClick={saveImage} variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCloseImageModal: PropTypes.func.isRequired,
  onImageSaved: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
